/**
 * Customer discovery listability cache (sync).
 *
 * Shared eligibility with backend Obchody + contacts + public tenants:
 * tenants with DISABLED surface_customer OR product_vending are unlistable.
 *
 * Two seed modes:
 * - **complete** (`rememberPublicCustomerListableTenantCodes`): full public
 *   tenants replace-seed. Absent codes ⇒ unlistable (`false`).
 * - **positive** (`unionPublicCustomerListableTenantCodes` / SP directory):
 *   positive evidence only. Seen codes ⇒ listable (`true`); unseen stay
 *   unknown (`null`) — never false just because they were not on pages
 *   fetched so far.
 *
 * Marked unlistable from settled entitlement deny for home JWT — including
 * platform chrome (`__platform__`), where entitlements remap to jwt home.
 */
export const CUSTOMER_DISCOVERY_LISTABLE_BLOCK_KEYS = [
  'surface_customer',
  'product_vending',
] as const;

export const CUSTOMER_DISCOVERY_LISTABILITY_CHANGED_EVENT =
  'customer:discoveryListability-changed';

/** Complete replace-seed vs positive-evidence SP union. */
export type CustomerDiscoveryListableSeedMode = 'complete' | 'positive';

let publicListableSeed: Set<string> | null = null;
/** Meaningful only when `publicListableSeed !== null`. */
let publicListableSeedMode: CustomerDiscoveryListableSeedMode | null = null;
const knownUnlistable = new Set<string>();

/** Obchody SP allowlist (same SoT as Favorites / `/shops`) — positive or complete. */
let listableSalesPointIds: Set<number> | null = null;
let listableTenantSlugKeys: Set<string> | null = null;
/** Meaningful only when SP sets are non-null. */
let salesPointAllowlistMode: CustomerDiscoveryListableSeedMode | null = null;

function normalizeTenantCode(code: string): string {
  return code.trim().toLowerCase();
}

/** Prefer salesPointSlug, else code — null when neither is usable. */
function resolveDirectorySlugCandidate(
  salesPointSlug: string | null | undefined,
  code: string | null | undefined,
): string | null {
  if (typeof salesPointSlug === 'string' && salesPointSlug.trim().length > 0) {
    return salesPointSlug;
  }
  if (typeof code === 'string' && code.trim().length > 0) {
    return code;
  }
  return null;
}

/** Same key shape as `tenantSlugListableKey` / public allowlist drain. */
export function customerDiscoveryTenantSlugKey(
  tenantCode: string,
  slugOrCode: string,
): string {
  return `${normalizeTenantCode(tenantCode)}|${slugOrCode.trim().toLowerCase()}`;
}

function notifyListabilityChanged(): void {
  if (typeof window === 'undefined') {
    return;
  }
  window.dispatchEvent(new Event(CUSTOMER_DISCOVERY_LISTABILITY_CHANGED_EVENT));
}

/** Subscribe for BottomNav / resolveShopHref recomputation. */
export function subscribeCustomerDiscoveryListability(onChange: () => void): () => void {
  if (typeof window === 'undefined') {
    return () => undefined;
  }
  window.addEventListener(CUSTOMER_DISCOVERY_LISTABILITY_CHANGED_EVENT, onChange);
  return () => {
    window.removeEventListener(CUSTOMER_DISCOVERY_LISTABILITY_CHANGED_EVENT, onChange);
  };
}

export function getCustomerDiscoveryListabilitySnapshot(): string {
  const seed =
    publicListableSeed === null
      ? ''
      : [...publicListableSeed].sort().join(',');
  const mode = publicListableSeedMode ?? '';
  const unlistable = [...knownUnlistable].sort().join(',');
  const spMode = salesPointAllowlistMode ?? '';
  const spIds =
    listableSalesPointIds === null
      ? ''
      : [...listableSalesPointIds].sort((a, b) => a - b).join(',');
  const spSlugs =
    listableTenantSlugKeys === null
      ? ''
      : [...listableTenantSlugKeys].sort().join(',');
  return `${mode}|${seed}|${unlistable}|${spMode}|${spIds}|${spSlugs}`;
}

export function getCustomerDiscoveryListabilityServerSnapshot(): string {
  return '';
}

/**
 * Replace public-directory listable codes (SoT from ListPublicCustomerTenants).
 * Complete snapshot: omitted codes are unlistable (`false`).
 */
export function rememberPublicCustomerListableTenantCodes(codes: readonly string[]): void {
  publicListableSeed = new Set(
    codes.map(normalizeTenantCode).filter((code) => code.length > 0),
  );
  publicListableSeedMode = 'complete';
  for (const code of publicListableSeed) {
    knownUnlistable.delete(code);
  }
  notifyListabilityChanged();
}

/**
 * Union listable codes as positive evidence (Obchody multi-page / tenant-scoped SP).
 * Empty input is a no-op. Does not promote seed to complete — unseen codes stay
 * unknown (`null`) unless a prior complete replace-seed already applies.
 * If a complete seed already exists, union only adds listable codes (mode stays complete).
 */
export function unionPublicCustomerListableTenantCodes(codes: readonly string[]): void {
  const normalized = [
    ...new Set(codes.map(normalizeTenantCode).filter((code) => code.length > 0)),
  ];
  if (normalized.length === 0) {
    return;
  }
  if (publicListableSeed === null) {
    publicListableSeed = new Set(normalized);
    publicListableSeedMode = 'positive';
  } else {
    for (const code of normalized) {
      publicListableSeed.add(code);
    }
    // Keep 'complete' if already complete; otherwise stay 'positive'.
    if (publicListableSeedMode === null) {
      publicListableSeedMode = 'positive';
    }
  }
  for (const code of normalized) {
    knownUnlistable.delete(code);
  }
  notifyListabilityChanged();
}

/**
 * Union Obchody SP ids / tenant|code keys as positive evidence (page fetches).
 * Empty input is a no-op. Unseen SPs stay unknown (`null`) unless a complete
 * replace-seed already applies.
 */
export function unionPublicCustomerListableSalesPoints(
  entries: readonly {
    readonly salesPointId?: number;
    readonly tenantCode: string;
    readonly code?: string | null;
    readonly salesPointSlug?: string | null;
  }[],
): void {
  if (entries.length === 0) {
    return;
  }
  if (listableSalesPointIds === null || listableTenantSlugKeys === null) {
    listableSalesPointIds = new Set();
    listableTenantSlugKeys = new Set();
    salesPointAllowlistMode = 'positive';
  } else if (salesPointAllowlistMode === null) {
    salesPointAllowlistMode = 'positive';
  }
  const ids = listableSalesPointIds;
  const slugKeys = listableTenantSlugKeys;
  // Keep 'complete' if already complete; otherwise stay/become 'positive'.
  for (const entry of entries) {
    if (
      typeof entry.salesPointId === 'number' &&
      Number.isFinite(entry.salesPointId) &&
      entry.salesPointId > 0
    ) {
      ids.add(Math.floor(entry.salesPointId));
    }
    const slug = resolveDirectorySlugCandidate(entry.salesPointSlug, entry.code);
    if (slug !== null && entry.tenantCode.trim().length > 0) {
      slugKeys.add(customerDiscoveryTenantSlugKey(entry.tenantCode, slug));
    }
  }
  notifyListabilityChanged();
}

/**
 * Complete replace-seed of Obchody SP allowlist (full directory drain).
 * Omitted SPs are unlistable (`false`) — same fail-closed semantics as Favorites.
 */
export function rememberPublicCustomerListableSalesPoints(allowlist: {
  readonly salesPointIds: readonly number[];
  readonly tenantSlugKeys: readonly string[];
}): void {
  listableSalesPointIds = new Set(
    allowlist.salesPointIds.filter(
      (id) => typeof id === 'number' && Number.isFinite(id) && id > 0,
    ).map((id) => Math.floor(id)),
  );
  listableTenantSlugKeys = new Set(
    allowlist.tenantSlugKeys
      .map((key) => key.trim().toLowerCase())
      .filter((key) => key.includes('|')),
  );
  salesPointAllowlistMode = 'complete';
  notifyListabilityChanged();
}

/**
 * Seed listable tenant codes (+ SP ids/slugs when present) from public directory rows.
 * Positive evidence only. Empty `items` does not mark unlistable.
 */
export function rememberListableTenantCodesFromSalesPoints(
  items: readonly {
    readonly tenantCode: string;
    readonly salesPointId?: number;
    readonly code?: string | null;
    readonly salesPointSlug?: string | null;
  }[],
): void {
  unionPublicCustomerListableTenantCodes(items.map((item) => item.tenantCode));
  unionPublicCustomerListableSalesPoints(items);
}

/** Mark a tenant unlistable (settled entitlement deny for home JWT, any chrome). */
export function markTenantCustomerDiscoveryUnlistable(tenantCode: string): void {
  const code = normalizeTenantCode(tenantCode);
  if (code.length === 0) {
    return;
  }
  if (knownUnlistable.has(code)) {
    return;
  }
  knownUnlistable.add(code);
  notifyListabilityChanged();
}

/**
 * `true` listable · `false` unlistable · `null` unknown.
 *
 * - Marked unlistable → `false`
 * - In seed → `true`
 * - Complete replace-seed and absent → `false`
 * - Positive-only SP union and absent → `null` (not yet seen — not DISABLED)
 * - No seed → `null`
 *
 * Home / favorites entry fail-closes on unknown: `resolveShopHref` treats `null`
 * as not a JWT shop destination until seed/mark proves listable (`=== true`).
 */
export function isTenantCustomerDiscoveryListable(tenantCode: string): boolean | null {
  const code = normalizeTenantCode(tenantCode);
  if (code.length === 0) {
    return false;
  }
  if (knownUnlistable.has(code)) {
    return false;
  }
  if (publicListableSeed === null) {
    return null;
  }
  if (publicListableSeed.has(code)) {
    return true;
  }
  // Absent from seed: deny only for complete public-tenants snapshot.
  if (publicListableSeedMode === 'complete') {
    return false;
  }
  return null;
}

/**
 * Obchody /shops SP listability for Purchases lastKnown deep-link (same gate as Favorites).
 *
 * `true` on allowlist · `false` when tenant marked unlistable or complete seed omits SP ·
 * `null` unknown (positive-only / no seed). Purchases fail-closes on `!== true`.
 */
export function isSalesPointCustomerDiscoveryListable(input: {
  readonly salesPointId?: number | null;
  readonly tenantCode: string;
  readonly code?: string | null;
  readonly salesPointSlug?: string | null;
}): boolean | null {
  const tenantListable = isTenantCustomerDiscoveryListable(input.tenantCode);
  if (tenantListable === false) {
    return false;
  }

  const id =
    typeof input.salesPointId === 'number' &&
    Number.isFinite(input.salesPointId) &&
    input.salesPointId > 0
      ? Math.floor(input.salesPointId)
      : null;
  if (id !== null && listableSalesPointIds !== null && listableSalesPointIds.has(id)) {
    return true;
  }

  const slugRaw = resolveDirectorySlugCandidate(input.salesPointSlug, input.code);
  if (
    slugRaw !== null &&
    listableTenantSlugKeys !== null &&
    listableTenantSlugKeys.has(customerDiscoveryTenantSlugKey(input.tenantCode, slugRaw))
  ) {
    return true;
  }

  if (listableSalesPointIds === null || listableTenantSlugKeys === null) {
    return null;
  }
  if (salesPointAllowlistMode === 'complete') {
    return false;
  }
  return null;
}

/** Test / logout reset. */
export function clearCustomerDiscoveryListabilityForTests(): void {
  publicListableSeed = null;
  publicListableSeedMode = null;
  knownUnlistable.clear();
  listableSalesPointIds = null;
  listableTenantSlugKeys = null;
  salesPointAllowlistMode = null;
}
